import { t } from 'i18next';
import clsx from 'clsx';
import type { PageType, PaginatedEvents } from '../../../types/PhotoCardType';
import { calculateCollectedPhotoCardStats } from '../utils/CollectedPhotoCardCalculator';
import GradientAttentionIcon from '../icons/GradientAttentionIcon';
import type { InfiniteData } from 'react-query';

interface CollectedPhotoCardActionsBarProps {
  selectedCollectedData: Record<string, Record<string, { collectedCount: number }>>;
  currentMember: { name: string; memberId: string } | null;
  paginatedEvents: PaginatedEvents;
  data: InfiniteData<PageType>;
  onCompletedEditable: () => void;
  onRemoveCollectedCards: () => void;
}

const CollectedPhotoCardActionsBar = ({
  selectedCollectedData,
  currentMember,
  paginatedEvents,
  data,
  onCompletedEditable,
  onRemoveCollectedCards
}: CollectedPhotoCardActionsBarProps) => {
  const stats = calculateCollectedPhotoCardStats({
    currentMember,
    paginatedEvents,
    selectedCollectedData,
    data
  });

  const isDisabled = !stats.hasChanges;

  const baseButtonClass =
    'w-full py-[16px] rounded-[49px] font-semibold leading-none text-[17px] h-[52px]';
  const disabledClass =
    'bg-disabled-light dark:bg-disabled-dark text-onDisabled-light dark:text-onDisabled-dark';
  const confirmClass =
    'bg-secondary-light dark:bg-secondary-dark text-onSecondary-light dark:text-onSecondary-dark';
  const removeClass = 'bg-[#FFE7EA] dark:bg-[#492127] text-warning dark:text-[#FF495A]';

  return (
    <div
      className="flex flex-col items-center justify-center gap-[10px] fixed bottom-0 left-0 right-0 bg-pure-white dark:bg-pure-black pt-[14px] z-50 rounded-t-[20px] px-[24px] shadow-[0px_-1px_10px_0px_rgba(0,0,0,0.03),_0px_-1px_3px_0px_rgba(0,0,0,0.08)]"
      style={{
        paddingBottom: 'calc(10px + env(safe-area-inset-bottom))'
      }}
    >
      {isDisabled ? (
        <div className="flex items-center gap-[4px] h-[28px]">
          <GradientAttentionIcon width={22} height={22} className="my-[3px]" />
          <p className="flex items-center bg-gradient-single-red bg-clip-text text-transparent text-[15px] h-[18px] my-[5px] font-bold leading-none">
            {t('photoCardPage.changeToCollectedAlert')}
          </p>
        </div>
      ) : (
        <div className="flex flex-row items-center justify-between w-full leading-none h-[28px]">
          <p className="text-main-light-2 dark:text-main-dark-2 text-[15px] font-medium my-[5px]">
            {t('photoCardPage.selectedPhotoCard')}{' '}
            <span className="font-semibold">{stats.selectedCount}</span>
          </p>
          <div className="flex flex-row items-center mb-[4px] leading-none my-[5.5px]">
            <p className="text-main-light-2 dark:text-main-dark-2 text-[14px] font-medium ">
              🔮 {currentMember?.name} {stats.collectedPercentage}%{' '}
              <span className="text-main-light-4 dark:text-main-dark-4 text-[13px] font-normal">
                ({stats.collectedCount}/{stats.totalCount})
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="w-full flex gap-[8px]">
        <button
          type="button"
          className={clsx(baseButtonClass, isDisabled ? disabledClass : confirmClass)}
          onClick={onCompletedEditable}
          disabled={isDisabled}
        >
          {t('photoCardPage.changeToCollected')}
        </button>
        <button
          type="button"
          className={clsx(baseButtonClass, isDisabled ? disabledClass : removeClass)}
          onClick={onRemoveCollectedCards}
          disabled={isDisabled}
        >
          {t('photoCardPage.changeToUnCollected')}
        </button>
      </div>
    </div>
  );
};

export default CollectedPhotoCardActionsBar;